import React, { createContext, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import {
  fetchManagerById,
  fetchTrainerById,
  fetchMembersByIds,
  fetchTrainerTemplates,
  getTrainerExercises,
  fetchTrainerPrograms,
  getGymTrainers,
  fetchGroupsByIds,
  getGymManagers,
  getGymProducts,
  fetchGym,
  fetchAllTrainers,
  fetchAllManagers,
  sendVerificationCode,
  verifyLoginCode,
  VerificationCodeResponse,
} from '../api';
import {
  setSelectedGymId,
  setLoginManagerId,
  setLoginTrainerId,
  setLoginMemberId,
  setGymDashboardData,
  setExercise,
  setRemovedExercise,
  setRemovedGroup,
  setRemovedProgram,
  setRemovedTemplate,
  setGroup,
  setManagers,
  setTrainers,
  setMembers,
  setRemovedMember,
  setRemovedManager,
  setRemovedTrainer,
  setGym,
  setProgram,
  setTemplate,
  setGymProducts,
  resetAllState,
} from '../appState/stateActions';
import { useErrorContext } from './errorProvider';
import { useGlobalAction } from './globalActionProvider';
import {
  clearLoginStorageData,
  setLoginStorageData,
} from '../api/loginApiHelper';

import type { TrainerDashboardData } from '../appState/appInitialState';
import type {
  Gym,
  Exercise,
  TrainingRoutineTemplate,
  Member,
  Trainer,
  Manager,
  Program,
  Group,
} from '../types';

const getUniqueGymIds = (
  manager?: Manager | null,
  trainer?: Trainer | null,
  member?: Member | null,
): string[] => {
  return [
    ...new Set([
      ...(manager?.gymIds || []),
      ...(member?.gymIds || []),
      ...(trainer?.gymIds || []),
    ]),
  ];
};

export interface SessionActionContextProps {
  logInUser: (
    managerId?: string,
    trainerId?: string,
    memberId?: string,
  ) => Promise<boolean>;
  logout: () => void;
  isImpersonating: boolean;
  refreshExercise: (exercise: Exercise) => void;
  refreshExercisesAfterDelete: (exerciseId: string, trainerId: string) => void;
  refreshGroupsAfterDelete: (groupId: string, trainerId: string) => void;
  refreshProgramsAfterDelete: (programId: string, trainerId: string) => void;
  refreshTemplatesAfterDelete: (templateId: string, trainerId: string) => void;
  refreshGroup: (group: Group) => void;
  refreshMembers: (memberIds: string[]) => Promise<boolean>;
  removeMemberAssociations: (memberId: string, trainerIds: string[]) => void;
  refreshGym: (gym: Gym) => void;
  refreshManager: (manager: Manager) => void;
  removeManager: (managerId: string) => void;
  refreshTrainer: (trainer: Trainer) => void;
  refreshMember: (member: Member) => void;
  refreshProgram: (program: Program) => void;
  refreshTemplate: (template: TrainingRoutineTemplate) => void;
  refreshGymProducts: (gymId: string) => Promise<boolean>;
  removeTrainer: (trainerId: string) => void;
  sendLoginVerificationCode: (email: string) => Promise<boolean>;
  getLoginCodeVerification: (
    email: string,
    code: string,
  ) => Promise<VerificationCodeResponse | null>;
  lookupAccountByEmail: (
    email: string,
  ) => Promise<VerificationCodeResponse | null>;
  impersonateAccount: (account: VerificationCodeResponse) => Promise<boolean>;
}

const SessionActionContext = createContext<SessionActionContextProps>({
  logInUser: () => Promise.resolve(false),
  logout: () => {},
  isImpersonating: false,
  refreshExercise: () => {},
  refreshExercisesAfterDelete: () => {},
  refreshGroupsAfterDelete: () => {},
  refreshProgramsAfterDelete: () => {},
  refreshTemplatesAfterDelete: () => {},
  refreshGroup: () => {},
  refreshMembers: () => Promise.resolve(false),
  removeMemberAssociations: () => {},
  refreshGym: () => {},
  refreshManager: () => {},
  removeManager: () => {},
  refreshTrainer: () => {},
  refreshMember: () => {},
  refreshProgram: () => {},
  refreshTemplate: () => {},
  refreshGymProducts: () => Promise.resolve(false),
  removeTrainer: () => {},
  sendLoginVerificationCode: () => Promise.resolve(false),
  getLoginCodeVerification: () => Promise.resolve(null),
  lookupAccountByEmail: () => Promise.resolve(null),
  impersonateAccount: () => Promise.resolve(false),
} as SessionActionContextProps);

export const SessionActionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { setGlobalError } = useErrorContext();
  const { loadSessionExercises, loadSessionMuscles } = useGlobalAction();
  const [isImpersonating, setIsImpersonating] = useState(false);

  const getTrainerDashboardData = async (
    trainer: Trainer,
  ): Promise<TrainerDashboardData> => {
    const [
      { data: members, success: membersSuccess },
      { data: groups, success: groupsSuccess },
      { data: personalizedExercises, success: personalizedExercisesSuccess },
      { data: templates, success: templatesSuccess },
      { data: programs, success: programsSuccess },
    ] = await Promise.all([
      fetchMembersByIds(trainer.memberIds),
      fetchGroupsByIds(trainer.groupIds),
      getTrainerExercises([trainer.id]),
      fetchTrainerTemplates(trainer.id),
      fetchTrainerPrograms(trainer.id),
    ]);

    if (
      membersSuccess &&
      groupsSuccess &&
      personalizedExercisesSuccess &&
      templatesSuccess &&
      programsSuccess
    ) {
      const trainerDashboardData = {
        trainer,
        members: _.keyBy(
          (members ?? []).filter(({ isActive }) => isActive),
          'id',
        ),
        groups: _.keyBy(groups ?? [], 'id'),
        personalizedExercises: _.keyBy(personalizedExercises ?? [], 'id'),
        templates: _.keyBy(templates ?? [], 'id'),
        programs: _.keyBy(programs ?? [], 'id'),
      };
      return trainerDashboardData;
    } else {
      setGlobalError('Error loading trainer data: ');
      return {
        trainer,
        members: {},
        groups: {},
        programs: {},
        templates: {},
        personalizedExercises: {},
      };
    }
  };

  const logInUser = async (
    managerId?: string,
    trainerId?: string,
    memberId?: string,
  ): Promise<boolean> => {
    if (managerId) {
      dispatch(setLoginManagerId(managerId));
    }
    if (trainerId) {
      dispatch(setLoginTrainerId(trainerId));
    }
    if (memberId) {
      dispatch(setLoginMemberId(memberId));
    }

    const [managerResult, trainerResult] = await Promise.all([
      managerId ? fetchManagerById(managerId) : Promise.resolve(null),
      trainerId ? fetchTrainerById(trainerId) : Promise.resolve(null),
    ]);

    if (managerId && (!managerResult || !managerResult.success)) {
      setGlobalError(
        managerResult?.error?.message || 'Unable to load manager profile',
      );
      return false;
    }

    if (trainerId && (!trainerResult || !trainerResult.success)) {
      setGlobalError(
        trainerResult?.error?.message || 'Unable to load trainer profile',
      );
      return false;
    }

    const managerData = managerResult?.data ?? null;
    const trainerData = trainerResult?.data ?? null;

    if (trainerId && !trainerData) {
      setGlobalError('Trainer profile not found');
      return false;
    }

    if (managerId && !managerData) {
      setGlobalError('Manager profile not found');
      return false;
    }

    const allGymIds = getUniqueGymIds(managerData, trainerData);

    if (allGymIds.length === 0) {
      setGlobalError('No gyms found');
      return false;
    }

    dispatch(setSelectedGymId(allGymIds[0]));

    await Promise.all([
      loadSessionMuscles(),
      loadSessionExercises(),
      // gymDataMap here
      ...allGymIds.map(async (gymId) => {
        const [
          { data: gym },
          { data: trainers },
          { data: managers },
          { data: gymProducts },
        ] = await Promise.all([
          fetchGym(gymId),
          getGymTrainers(gymId),
          getGymManagers(gymId),
          getGymProducts(gymId),
        ]);

        if (!gym) {
          setGlobalError('Error fetching gym');
          return;
        }

        const activeManagers = (managers ?? []).filter(
          ({ isActive }) => isActive,
        );
        const activeTrainers = (trainers ?? []).filter(
          ({ isActive }) => isActive,
        );

        const trainersDashboardData = await Promise.all(
          activeTrainers.map(async (trainer) =>
            getTrainerDashboardData(trainer),
          ),
        );

        dispatch(
          setGymDashboardData({
            gym,
            managers: _.keyBy(activeManagers, 'id'),
            trainers: _.keyBy(trainersDashboardData, 'trainer.id'),
            unassignedMembers: {},
            gymProducts: gymProducts ?? [],
          }),
        );
      }),
    ]);

    return Boolean(managerData || trainerData);
  };

  const refreshExercise = (exercise: Exercise): void => {
    dispatch(setExercise(exercise));
  };

  const refreshExercisesAfterDelete = (
    exerciseId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedExercise(exerciseId, trainerId));
  };

  const refreshGroupsAfterDelete = (
    groupId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedGroup(groupId, trainerId));
  };

  const refreshProgramsAfterDelete = (
    programId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedProgram(programId, trainerId));
  };

  const refreshTemplatesAfterDelete = (
    templateId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedTemplate(templateId, trainerId));
  };

  const refreshGroup = (group: Group): void => {
    dispatch(setGroup(group));
  };

  const refreshMembers = async (memberIds: string[]): Promise<boolean> => {
    if (!memberIds?.length) {
      return true;
    }
    const {
      data: members,
      success,
      error,
    } = await fetchMembersByIds(memberIds);
    if (!success || !members) {
      setGlobalError(error?.message || 'Error refreshing members');
      return false;
    }
    dispatch(setMembers(members));
    return true;
  };

  const removeMemberAssociations = (
    memberId: string,
    trainerIds: string[],
  ): void => {
    if (!trainerIds?.length) {
      return;
    }
    dispatch(setRemovedMember(memberId, trainerIds));
  };

  const refreshGym = (gym: Gym): void => {
    dispatch(setGym(gym));
  };

  const refreshManager = (manager: Manager): void => {
    dispatch(setManagers([manager]));
  };

  const removeManager = (managerId: string): void => {
    if (!managerId) {
      return;
    }
    dispatch(setRemovedManager(managerId));
  };

  const refreshTrainer = (trainer: Trainer): void => {
    dispatch(setTrainers([trainer]));
  };

  const removeTrainer = (trainerId: string): void => {
    if (!trainerId) {
      return;
    }
    dispatch(setRemovedTrainer(trainerId));
  };

  const refreshMember = (member: Member): void => {
    dispatch(setMembers([_.cloneDeep(member)]));
  };

  const refreshProgram = (program: Program): void => {
    dispatch(setProgram(_.cloneDeep(program)));
  };

  const refreshTemplate = (template: TrainingRoutineTemplate): void => {
    dispatch(setTemplate(_.cloneDeep(template)));
  };

  const refreshGymProducts = async (gymId: string): Promise<boolean> => {
    if (!gymId) {
      setGlobalError('Gym id is required to refresh products');
      return false;
    }
    const { data, success, error } = await getGymProducts(gymId);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error refreshing gym products');
      return false;
    }
    dispatch(setGymProducts(gymId, data));
    return true;
  };

  const sendLoginVerificationCode = async (email: string): Promise<boolean> => {
    const { success, error } = await sendVerificationCode(email);
    if (!success) {
      setGlobalError(error?.message || 'Error sending verification code');
    }
    return success;
  };

  const getLoginCodeVerification = async (
    email: string,
    code: string,
  ): Promise<VerificationCodeResponse | null> => {
    const { data, success, error } = await verifyLoginCode(email, code);
    if (!success) {
      setGlobalError(error?.message || 'Error verifying login code');
    }
    return data;
  };

  const lookupAccountByEmail = async (
    email: string,
  ): Promise<VerificationCodeResponse | null> => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
      setGlobalError('Email is required');
      return null;
    }
    // NOTE: These fetch-all calls are intentionally limited to the developer-only
    // impersonation tool so we can search every Motivision account without
    // exposing these heavy endpoints elsewhere in the app.
    const [trainersResponse, managersResponse] = await Promise.all([
      fetchAllTrainers(),
      fetchAllManagers(),
    ]);
    if (!trainersResponse.success || !trainersResponse.data) {
      setGlobalError(
        trainersResponse.error?.message ||
          'Error retrieving trainers for impersonation',
      );
      return null;
    }
    const trainer = trainersResponse.data.find(
      (candidate) => candidate.email?.toLowerCase() === normalizedEmail,
    );
    if (!trainer) {
      return null;
    }

    let managerId: string | undefined;
    if (managersResponse.success && managersResponse.data) {
      const manager = managersResponse.data.find(
        (candidate) => candidate.email?.toLowerCase() === normalizedEmail,
      );
      managerId = manager?.id;
    } else if (!managersResponse.success) {
      setGlobalError(
        managersResponse.error?.message ||
          'Error retrieving managers for impersonation',
      );
    }

    return {
      success: true,
      trainerId: trainer.id,
      managerId,
      memberId: undefined,
    };
  };

  const impersonateAccount = async (
    account: VerificationCodeResponse,
  ): Promise<boolean> => {
    if (!account?.trainerId) {
      setGlobalError('Trainer ID is required for impersonation');
      return false;
    }

    setIsImpersonating(true);
    try {
      dispatch(resetAllState());
      clearLoginStorageData();
      const loginStorageData = {
        managerId: account.managerId,
        trainerId: account.trainerId,
        memberId: account.memberId,
        loginExpiration:
          account.loginExpiration ?? Date.now() + 1000 * 60 * 60 * 24 * 30,
      };

      setLoginStorageData(loginStorageData);
      const success = await logInUser(
        loginStorageData.managerId,
        loginStorageData.trainerId,
        loginStorageData.memberId,
      );

      if (!success) {
        clearLoginStorageData();
        setGlobalError('Unable to impersonate selected account');
      }

      return success;
    } finally {
      setIsImpersonating(false);
    }
  };

  const logout = (): void => {
    clearLoginStorageData();
    dispatch(resetAllState());
  };

  return (
    <SessionActionContext.Provider
      value={{
        logInUser,
        logout,
        isImpersonating,
        refreshExercise,
        refreshExercisesAfterDelete,
        refreshGroupsAfterDelete,
        refreshProgramsAfterDelete,
        refreshTemplatesAfterDelete,
        refreshGroup,
        refreshMembers,
        removeMemberAssociations,
        refreshGym,
        refreshManager,
        removeManager,
        refreshTrainer,
        refreshMember,
        refreshProgram,
        refreshTemplate,
        refreshGymProducts,
        removeTrainer,
        sendLoginVerificationCode,
        getLoginCodeVerification,
        lookupAccountByEmail,
        impersonateAccount,
      }}
    >
      {children}
    </SessionActionContext.Provider>
  );
};

export const useSessionAction = () => {
  return useContext(SessionActionContext);
};
